import { createRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { Button, Dropdown, FormRow, Label, QueryDropdown, TextField } from 'basic-components';
import { Add as AddIcon } from 'basic-components/icons';
import { DataLakeEnums, DataLakeReadableConsts } from 'dashboard-services/api/dataLake';
import Query from 'dashboard-services/api/Query';
import { ValidationService } from 'dashboard-services/services';
import { DropdownService, globalActions } from 'primary-components';

import { basicUploadActions, fileUploadActions } from 'actions/upload';
import getApiConfig from 'api/ApiConfig';
import { groupsDatalakeQueries } from 'queries/datalake';

import MetadataRow from './MetadataRow';

import { useQueryClient } from '@tanstack/react-query';

const useCreateGroupCall = dispatch => {
  const getQueryBuilderWithValidation = useCallback(() => 
          new Query.Builder(dispatch(getApiConfig()))
            .withOnError(({ response }) => ValidationService.validateInput({ response, setValidationErrors: basicUploadActions.setErrors, onOtherError: () => dispatch(globalActions.getMessageStore()).error }))
        , [dispatch]),
        { mutateAsync: createGroupCall } = groupsDatalakeQueries.useCreateGroup({ getQueryBuilder: getQueryBuilderWithValidation })

  return createGroupCall;
}

const emptyArray = []
const EXTENSION = ".csv"
const FileConfig = ({ onChangeRaw, fileNameRef, groupNameRef, fileTypeRef }) => {
  const dispatch = useDispatch(),
        {
          errors,
          groupName,
          fileName,
          fileType,
          fields,
        } = useSelector(state => state.uploadState),
        [refs, setRefs] = useState([]),
        queryClient = useQueryClient(),
        getApiConfigLocal = useCallback(() => dispatch(getApiConfig()), [dispatch]),
        [groupQuery, setGroupQuery] = useState(),
        { fetchNextPage: fetchMoreGroups, isFetching: isFetchingGroups, isFetchingNextPage: isFetchingNextPageGroups, data: groups = emptyArray } = DropdownService.useQueryGroups({
          queryClient,
          input: groupQuery,
          getApiConfig: getApiConfigLocal,
          writeOnly: true,
          useV2: true
        }),
        localFileTypes = useMemo(() => {
          const defaultTypes = [
            DataLakeReadableConsts.FILE_TYPES.find(n => n.value === DataLakeEnums.FILE_TYPE.SOURCE),
            DataLakeReadableConsts.FILE_TYPES.find(n => n.value === DataLakeEnums.FILE_TYPE.NCSV),
            DataLakeReadableConsts.FILE_TYPES.find(n => n.value === DataLakeEnums.FILE_TYPE.D_NCSV),
          ]
          return defaultTypes;
        }, []),
        defaultFileTypeObj = useMemo(() => localFileTypes.find(l => l.value === fileType), [fileType, localFileTypes]),
        addField = useCallback(() => dispatch(fileUploadActions.addField()), [dispatch]),
        getGroupNameValidator = useMemo(() => [{
          message: errors?.find(e => e.field === `groupName`)?.message || "",
          validator: () => errors?.findIndex(e => e.field === `groupName`) === -1
        }], [errors]),
        createGroupCall = useCreateGroupCall(dispatch),
        onGroupCreate = useCallback(val => 
          dispatch(basicUploadActions.onGroupCreate({ value: val, createGroupCall }))
            .then(() => onChangeRaw(val, { name: 'groupName' }))
        , [createGroupCall, dispatch, onChangeRaw]),
        groupRef = useRef(),
        onChangeGroupName = useCallback((value, { data }) => {
          if(data?.isPublic) {
            dispatch(globalActions.getToastsStore()).warning("The selected group is public. Do you want to proceed?", "Selected group is public", {
              withCancel: false,
              confirmText: "Confirm",
              confirmAction: () => {
                onChangeRaw(value, { name: 'groupName' })
              },
              cancelAction: () => {
                groupRef.current.dropdown.current.setValue(groupName)
              }
            })
          } else {
            onChangeRaw(value, { name: 'groupName' })
          }
        }, [dispatch, groupName, onChangeRaw]),
        getNameOnFocus = useCallback(() => {
          const newValue = fileName || ""
          if(newValue?.endsWith(EXTENSION)) {
            return newValue.replace(/\.[^/.]+$/, "")
          } else {
            return newValue
          }
        }, [fileName]),
        onFocus = useCallback(() => onChangeRaw(getNameOnFocus(), { name: "fileName" }), [getNameOnFocus, onChangeRaw]),
        getNameOnBlur = useCallback(() => {
          const newValue = fileName || ""
          if(newValue?.endsWith(EXTENSION) || newValue === "") {
            return newValue.trim()
          } else {
            return newValue.trim() + EXTENSION
          }
        }, [fileName]),
        onBlur = useCallback(() => onChangeRaw(getNameOnBlur(), { name: "fileName" }), [getNameOnBlur, onChangeRaw])

  useEffect(() => {
    setRefs(refs => (
      Array(fields.length).fill().map((_, i) => refs[i] || createRef())
    ));
  }, [fields?.length])

  useEffect(() => {
    const lastRef = refs[fields.length - 1]
    lastRef?.current && lastRef.current.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [refs, fields?.length])

  return (
    <div className="ng-office-app__authed__content__body__section">
      <div className="ng-office-app__authed__content__body__section__title">
        FILE CONFIGURATION
      </div>
      <FormRow 
          className="ng-office-app__authed__content__body__section__body"
          isVertical
      >
        <Label>File name</Label>
        <TextField
            defaultValue={fileName}
            name="fileName"
            onBlur={onBlur}
            onChange={onChangeRaw}
            onFocus={onFocus}
            placeholder="Create file name"
            ref={fileNameRef}
            required
            variant="border"
        />
        <Label>Upload group</Label>
        <QueryDropdown
            allowCreateWhileLoading={false}
            creatable
            defaultValue={groupName}
            fetchNextPage={fetchMoreGroups}
            fixed
            isFetching={isFetchingGroups || isFetchingNextPageGroups}
            menuPortalTarget={document.querySelector('.ng-office-app')}
            name="groupName"
            onChange={onChangeGroupName}
            onCreateOption={onGroupCreate}
            onInputChange={setGroupQuery}
            options={groups}
            placeholder="Choose upload group or type to create new"
            ref={groupNameRef}
            required
            size="large"
            validators={getGroupNameValidator}
            variant="border"
        />
        <Label>File type</Label>
        <Dropdown
            defaultValue={defaultFileTypeObj}
            menuPortalTarget={document.querySelector('.ng-office-app')}
            name="fileType"
            onChange={onChangeRaw}
            options={localFileTypes}
            ref={fileTypeRef}
            required
            variant="border"
        />
        <Label>Metadata</Label>
        <div 
            className="ng-office-app__authed__content__body__section__body__icon-button"
            onClick={addField}
        >
          <AddIcon
              color="blue-bright"
              height={20}
              width={20}
          />
          <Button
              size="small"
              variant="text"
          >
            Add metadata set
          </Button>
        </div>
        {fields.map((field, index) => (
          <MetadataRow
              index={index}
              key={"Metadata" + index}
              ref={refs[index]}
          />
        ))}
      </FormRow>
    </div>
  )
}

FileConfig.propTypes = {
  fileNameRef: PropTypes.any.isRequired,
  fileTypeRef: PropTypes.any.isRequired,
  groupNameRef: PropTypes.any.isRequired,
  onChangeRaw: PropTypes.func.isRequired
}

export default FileConfig